import { useEffect, useRef, useState } from 'react';
import { readError } from '../../app/api';
import { loadAppVersions } from './analytics-api';
import type { AnalyticsFilterScope } from './AnalyticsFilterScope';
import type { AppVersionOption } from './types';

export function AppVersionMultiFilter(props: {
  environmentId: string;
  filters: AnalyticsFilterScope;
  selectedVersions: readonly string[];
  onChange: (versions: string[]) => void;
}): React.JSX.Element {
  const details = useRef<HTMLDetailsElement>(null);
  const [options, setOptions] = useState<AppVersionOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    function closeOnOutsideClick(event: MouseEvent): void {
      const picker = details.current;
      if (picker !== null && picker.open && event.target instanceof Node && !picker.contains(event.target)) {
        picker.open = false;
      }
    }

    document.addEventListener('click', closeOnOutsideClick);
    return () => { document.removeEventListener('click', closeOnOutsideClick); };
  }, []);

  useEffect(() => {
    let active = true;
    setOptions([]);
    setLoading(true);
    setError('');
    loadAppVersions(props.environmentId, props.filters)
      .then((response) => { if (active) setOptions(response); })
      .catch((caught: unknown) => { if (active) setError(readError(caught)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [
    props.environmentId,
    props.filters.preset,
    props.filters.from,
    props.filters.to,
    props.filters.platforms,
    props.filters.countries,
  ]);

  const selectedCount = props.selectedVersions.length;
  const summary = selectedCount === 0
    ? 'All versions'
    : props.selectedVersions.join(', ');
  const knownVersions = new Set(options.map(readVersion));
  const unavailableSelections = props.selectedVersions.filter((version) => !knownVersions.has(version));

  return (
    <section className="analytics-option-filter app-version-filter" aria-label="App versions included">
      <strong>App versions</strong>
      <details className="analytics-option-picker" ref={details}>
        <summary title={selectedCount > 0 ? summary : undefined}>
          <span>{summary}</span>
          <small>{selectedCount === 0 ? 'No version filter' : `${formatNumber(selectedCount)} selected`}</small>
        </summary>
        <div className="analytics-option-menu">
          <header className="analytics-option-menu-header"><span>Tracked versions</span><button type="button" onClick={close}>Done</button></header>
          <label className="analytics-option all-options">
            <input type="checkbox" checked={selectedCount === 0} onChange={showAll} />
            <span><strong>All versions</strong><small>{loading ? 'Loading tracked versions…' : `${formatNumber(options.length)} available`}</small></span>
          </label>
          {options.map(renderOption)}
          {unavailableSelections.map(renderUnavailableSelection)}
          {!loading && options.length === 0 && error === '' && <p>No versions match the other filters.</p>}
          {error !== '' && <p title={error}>Tracked versions could not be loaded.</p>}
        </div>
      </details>
    </section>
  );

  function showAll(): void {
    props.onChange([]);
  }

  function toggle(version: string): void {
    const selected = props.selectedVersions.includes(version);
    const next = selected
      ? props.selectedVersions.filter((entry) => entry !== version)
      : [...props.selectedVersions, version];
    props.onChange(next);
  }

  function close(): void {
    if (details.current !== null) details.current.open = false;
  }

  function renderOption(option: AppVersionOption): React.JSX.Element {
    const selected = props.selectedVersions.includes(option.version);
    return (
      <label className="analytics-option" key={option.version}>
        <input type="checkbox" checked={selected} onChange={() => toggle(option.version)} />
        <span><code>{option.version}</code><small>{formatNumber(option.events)} events · {formatNumber(option.sessions)} sessions</small></span>
      </label>
    );
  }

  function renderUnavailableSelection(version: string): React.JSX.Element {
    return (
      <label className="analytics-option unavailable" key={version}>
        <input type="checkbox" checked onChange={() => toggle(version)} />
        <span><code>{version}</code><small>Not available with the other filters</small></span>
      </label>
    );
  }
}

function readVersion(option: AppVersionOption): string {
  return option.version;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}
